import React, {
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import { Scrollbars } from "react-custom-scrollbars-2";

import { makeStyles } from "@mui/styles";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemButton from "@mui/material/ListItemButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import MuiAlert from "@mui/material/Alert";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import MovieRoundedIcon from "@mui/icons-material/MovieRounded";
import PlaylistPlayRoundedIcon from "@mui/icons-material/PlaylistPlayRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import VideocamOffRoundedIcon from "@mui/icons-material/VideocamOffRounded";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import { DatabaseContext, UserContext } from "../../context";
import firebase from "../../firebase";
import {
    shouldPlaylistBeVisible,
    shouldTimelineBeVisible,
} from "../../utility";
import ConfirmDeleteDialog from "../ConfirmDeleteDialog";
import DialogWithTextInput from "../DialogWithTextInput";
import ToolbarWithLogo from "../ToolbarWithLogo";
import { exportTimelineToXML } from "../analysis/timelineXml";
import { deletePlaylistOrTimelineRecord } from "./playlistTimelineDeletion";

const ROOT_ID = "Root";
const RECENT_ID = "recent-playlists-and-timelines";
const ALL_ITEMS_ID = "all-playlists-and-timelines";
const UNAVAILABLE_ID = "unavailable-playlist-sources";

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
    root: {
        height: "calc(100dvh - 64px)",
        padding: 0,
        margin: 0,
        overflow: "hidden",
        backgroundColor: theme.palette.background.default,
        [theme.breakpoints.down("sm")]: {
            height: "calc(100dvh - 60px)",
        },
    },
    searchBar: {
        height: 72,
        padding: "12px 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        backgroundColor:
            theme.palette.mode === "dark"
                ? "rgba(25, 32, 28, 0.96)"
                : "rgba(255, 255, 255, 0.96)",
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 2px 10px rgba(20, 35, 25, 0.05)",
        [theme.breakpoints.down("sm")]: {
            padding: "10px 12px",
        },
    },
    searchField: {
        width: "100%",
        maxWidth: 680,
        "& .MuiOutlinedInput-root": {
            height: 46,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.default,
            borderRadius: 12,
            transition:
                "background-color 120ms ease, box-shadow 120ms ease",
            "& fieldset": {
                borderColor: theme.palette.divider,
            },
            "&:hover": {
                backgroundColor: theme.palette.action.hover,
            },
            "&:hover fieldset": {
                borderColor: theme.palette.text.disabled,
            },
            "&.Mui-focused": {
                backgroundColor: theme.palette.background.paper,
                boxShadow: "0 0 0 3px rgba(0, 149, 61, 0.12)",
            },
            "&.Mui-focused fieldset": {
                borderColor: "rgb(0, 149, 61)",
                borderWidth: 1,
            },
        },
    },
    columnsContainer: {
        display: "flex",
        flexDirection: "row",
        minWidth: "100%",
        height: "100%",
        minHeight: 0,
        backgroundColor: theme.palette.background.default,
        [theme.breakpoints.down("sm")]: {
            height: "auto",
            minHeight: "100%",
            scrollSnapType: "x mandatory",
        },
    },
    columnShell: {
        flex: "0 0 25%",
        minWidth: "25%",
        maxWidth: "25%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        [theme.breakpoints.down("lg")]: {
            flexBasis: "33.3333%",
            minWidth: "33.3333%",
            maxWidth: "33.3333%",
        },
        [theme.breakpoints.down("md")]: {
            flexBasis: "50%",
            minWidth: "50%",
            maxWidth: "50%",
        },
        [theme.breakpoints.down("sm")]: {
            flexBasis: "100%",
            minWidth: "100%",
            maxWidth: "100%",
            height: "auto",
            minHeight: "100%",
            overflow: "visible",
            scrollSnapAlign: "start",
            scrollSnapStop: "always",
        },
    },
    column: {
        position: "relative",
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        overscrollBehaviorY: "contain",
        scrollbarGutter: "stable",
        scrollbarWidth: "thin",
        scrollbarColor: `${theme.palette.text.disabled} transparent`,
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        "&::-webkit-scrollbar": {
            width: 8,
        },
        "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.text.disabled,
            border: `2px solid ${theme.palette.background.paper}`,
            borderRadius: 999,
        },
        "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: theme.palette.text.secondary,
        },
        [theme.breakpoints.down("sm")]: {
            height: "auto",
            minHeight: "100%",
            overflowY: "visible",
            scrollbarGutter: "auto",
        },
    },
    titleRow: {
        position: "sticky",
        top: 0,
        zIndex: 2,
        minHeight: 58,
        padding: "8px 10px",
        display: "flex",
        alignItems: "center",
        backgroundColor:
            theme.palette.mode === "dark"
                ? "rgba(25, 32, 28, 0.96)"
                : "rgba(255, 255, 255, 0.96)",
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 2px 8px rgba(20, 35, 25, 0.04)",
        backdropFilter: "blur(8px)",
    },
    titleBlock: {
        minWidth: 0,
    },
    columnTitle: {
        display: "block",
        margin: 0,
        color: theme.palette.text.primary,
        fontFamily: "DraigRegular",
        fontSize: 18,
        lineHeight: 1.15,
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
    },
    columnMeta: {
        display: "block",
        marginTop: 3,
        color: theme.palette.text.secondary,
        fontSize: 12,
        lineHeight: 1,
    },
    sectionLabel: {
        padding: "13px 14px 3px",
        color: theme.palette.text.secondary,
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
    },
    rowContainer: {
        padding: "0 5px",
    },
    row: {
        width: "100%",
        minHeight: 60,
        margin: "5px 0",
        padding: "8px 10px",
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.subtle,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 10,
        boxSizing: "border-box",
        transition:
            "background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
        "& .MuiTouchRipple-rippleVisible": {
            opacity: "0.12 !important",
            animationDuration: "300ms !important",
        },
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.text.disabled,
            boxShadow: "0 3px 10px rgba(20, 35, 25, 0.07)",
        },
        "&:active": {
            transform: "translateY(0.5px)",
        },
        "&:focus-visible": {
            outline: "3px solid rgba(0, 149, 61, 0.28)",
            outlineOffset: 1,
        },
    },
    selectedRow: {
        color: "white",
        backgroundColor: theme.palette.mode === "dark" ? "#008035" : "rgb(0, 149, 61)",
        borderColor: theme.palette.mode === "dark" ? "#008035" : "rgb(0, 149, 61)",
        boxShadow: "0 4px 12px rgba(0, 105, 43, 0.18)",
        "&:hover": {
            backgroundColor: theme.palette.mode === "dark" ? "#00752f" : "rgb(0, 137, 56)",
            borderColor: theme.palette.mode === "dark" ? "#00752f" : "rgb(0, 137, 56)",
            boxShadow: "0 5px 14px rgba(0, 105, 43, 0.24)",
        },
    },
    ancestorRow: {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.action.selected,
        borderColor: theme.palette.divider,
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.text.disabled,
        },
    },
    iconTile: {
        position: "relative",
        zIndex: 1,
        width: 40,
        height: 40,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.action.hover,
        borderRadius: 8,
        "& .MuiSvgIcon-root": {
            display: "block",
            color: "currentColor",
            opacity: "1 !important",
            visibility: "visible",
        },
        "$selectedRow &": {
            color: "rgb(0, 118, 48)",
            backgroundColor: "rgba(255, 255, 255, 0.94)",
            boxShadow: "0 1px 4px rgba(0, 70, 28, 0.16)",
        },
        "$ancestorRow &": {
            backgroundColor: theme.palette.action.selected,
        },
    },
    thumbnail: {
        width: 40,
        height: 40,
        flexShrink: 0,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.action.hover,
        borderRadius: 8,
        "$selectedRow &": {
            color: "white",
            backgroundColor: "rgba(255, 255, 255, 0.16)",
        },
    },
    rowText: {
        minWidth: 0,
        flex: 1,
        marginLeft: 12,
    },
    rowTitle: {
        color: "inherit",
        fontSize: 16,
        lineHeight: 1.2,
        overflowWrap: "break-word",
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2,
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    rowDetails: {
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: 7,
        marginTop: 4,
    },
    ownershipBadge: {
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        padding: "2px 7px",
        color:
            theme.palette.mode === "dark"
                ? "rgb(87, 219, 139)"
                : "rgb(0, 112, 46)",
        backgroundColor:
            theme.palette.mode === "dark"
                ? "rgba(31, 184, 94, 0.16)"
                : "rgba(0, 149, 61, 0.1)",
        border:
            theme.palette.mode === "dark"
                ? "1px solid rgba(87, 219, 139, 0.38)"
                : "1px solid rgba(0, 149, 61, 0.16)",
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: "0.045em",
        lineHeight: 1.2,
        textTransform: "uppercase",
        "$selectedRow &": {
            color: "#fff",
            backgroundColor: "rgba(255, 255, 255, 0.16)",
            borderColor: "rgba(255, 255, 255, 0.24)",
        },
    },
    sharedBadge: {
        color:
            theme.palette.mode === "dark"
                ? "rgb(121, 184, 255)"
                : "rgb(48, 88, 132)",
        backgroundColor:
            theme.palette.mode === "dark"
                ? "rgba(64, 145, 230, 0.17)"
                : "rgba(48, 100, 158, 0.09)",
        borderColor:
            theme.palette.mode === "dark"
                ? "rgba(121, 184, 255, 0.38)"
                : "rgba(48, 100, 158, 0.15)",
        "$selectedRow &": {
            color: "#fff",
            backgroundColor: "rgba(255, 255, 255, 0.16)",
            borderColor: "rgba(255, 255, 255, 0.24)",
        },
    },
    rowMeta: {
        minWidth: 0,
        color: theme.palette.text.secondary,
        fontSize: 12,
        lineHeight: 1.15,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        "$selectedRow &": {
            color: "rgba(255, 255, 255, 0.82)",
        },
    },
    arrow: {
        marginLeft: 8,
        flexShrink: 0,
        fontSize: "20px !important",
        opacity: 0.78,
    },
    emptyState: {
        minHeight: 220,
        padding: 28,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: theme.palette.text.secondary,
        textAlign: "center",
    },
    emptyIcon: {
        marginBottom: 12,
        color: theme.palette.text.disabled,
        fontSize: "42px !important",
    },
    searchResults: {
        width: "100%",
        minHeight: "100%",
        padding: "18px clamp(10px, 3vw, 32px) 32px",
        boxSizing: "border-box",
        backgroundColor: theme.palette.background.default,
    },
    searchResultsCard: {
        width: "100%",
        maxWidth: 820,
        margin: "0 auto",
        padding: "8px 8px 18px",
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 16,
        boxShadow: "0 10px 28px rgba(20, 35, 25, 0.07)",
    },
    searchResultsHeader: {
        padding: "13px 14px 10px",
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    searchResultsTitle: {
        color: theme.palette.text.primary,
        fontFamily: "DraigRegular",
        fontSize: 20,
        lineHeight: 1.2,
    },
    searchResultsMeta: {
        marginTop: 4,
        color: theme.palette.text.secondary,
        fontSize: 13,
    },
    scrollView: {
        overflowY: "hidden !important",
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorX: "contain",
        [theme.breakpoints.down("sm")]: {
            overflowY: "auto !important",
            scrollSnapType: "x mandatory",
        },
    },
    libraryScroll: {
        width: "100% !important",
        height: "calc(100dvh - 200px) !important",
        [theme.breakpoints.down("sm")]: {
            height: "calc(100dvh - 132px) !important",
        },
    },
}));

function compareNames(a, b) {
    return (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
    });
}

function compareVideoNames(a, b) {
    return (a.displayName || "").localeCompare(b.displayName || "", undefined, {
        sensitivity: "base",
    });
}

function lastOpenedValue(item) {
    if (!item.lastOpened) return 0;
    const parsed = dayjs(item.lastOpened);
    return parsed.isValid() ? parsed.valueOf() : 0;
}

function createVideoNode(video, items, recentOnly = false) {
    const visibleItems = recentOnly
        ? items.filter((item) => lastOpenedValue(item) > 0)
        : items;

    return {
        ...video,
        linkedItems: [...visibleItems].sort((a, b) => {
            if (recentOnly) {
                return lastOpenedValue(b) - lastOpenedValue(a);
            }
            if (a.kind !== b.kind) return a.kind === "playlist" ? -1 : 1;
            return compareNames(a, b);
        }),
        recentOnly,
    };
}

export function buildPlaylistsAndTimelinesLibrary({
    files,
    folders,
    playlistInfo,
    playlistUsage,
    timelineInfo,
    timelineUsage = [],
    groups,
    users = [],
    currentUserData,
}) {
    const root = {
        id: ROOT_ID,
        key: ROOT_ID,
        name: "Everything",
        folders: [],
        videos: [],
    };

    if (!currentUserData) {
        return { root, itemCount: 0, videoCount: 0 };
    }

    const playlistUsageByKey = new Map(
        playlistUsage
            .filter((usage) => usage.user === currentUserData.key)
            .map((usage) => [usage.playlist, usage.lastOpened]),
    );

    const timelineUsageByKey = new Map(
        timelineUsage
            .filter((usage) => usage.user === currentUserData.key)
            .map((usage) => [usage.timeline, usage.lastOpened]),
    );

    const userNamesByKey = new Map(
        users.map((user) => [
            user.key,
            user.fullName ||
                [user.firstName, user.lastName].filter(Boolean).join(" "),
        ]),
    );

    const playlists = playlistInfo
        .filter((playlist) =>
            shouldPlaylistBeVisible(playlist, groups, currentUserData),
        )
        .map((playlist) => ({
            ...playlist,
            kind: "playlist",
            isOwner: playlist.owner === currentUserData.key,
            creatorName: userNamesByKey.get(playlist.owner),
            lastOpened: playlistUsageByKey.get(playlist.key),
        }));

    const timelines = timelineInfo
        .filter((timeline) =>
            shouldTimelineBeVisible(timeline, groups, currentUserData),
        )
        .map((timeline) => ({
            ...timeline,
            kind: "timeline",
            isOwner: timeline.owner === currentUserData.key,
            creatorName: userNamesByKey.get(timeline.owner),
            // Never timelineinfo.lastOpened, which is one value shared by
            // everyone who opens the timeline. Only timelineusage says when
            // THIS user opened it.
            lastOpened: timelineUsageByKey.get(timeline.key),
        }));

    const allItems = [...playlists, ...timelines];
    const itemsByVideo = new Map();

    allItems.forEach((item) => {
        if (!itemsByVideo.has(item.video)) itemsByVideo.set(item.video, []);
        itemsByVideo.get(item.video).push(item);
    });

    const fileMap = new Map(files.map((file) => [file.key, file]));
    const folderMap = new Map(folders.map((folder) => [folder.key, folder]));
    const folderNodes = new Map();

    function ensureFolder(folderKey, trail = new Set()) {
        if (!folderKey || folderKey === ROOT_ID) return root;
        if (folderNodes.has(folderKey)) return folderNodes.get(folderKey);
        if (trail.has(folderKey)) return root;

        const folder = folderMap.get(folderKey);
        if (!folder) return root;

        const nextTrail = new Set(trail);
        nextTrail.add(folderKey);

        const node = {
            ...folder,
            id: folder.key,
            folders: [],
            videos: [],
        };
        folderNodes.set(folderKey, node);

        const parent = ensureFolder(folder.parent, nextTrail);
        parent.folders.push(node);
        return node;
    }

    const recentVideos = [];
    const unavailableVideos = [];
    // Every video with something linked to it, flat. The folder tree here runs
    // eight levels deep in places, so this is the only view that shows a
    // person everything they own without knowing where it was filed.
    const allVideos = [];

    itemsByVideo.forEach((items, videoKey) => {
        const sourceVideo = fileMap.get(videoKey);

        if (sourceVideo) {
            const videoNode = createVideoNode(sourceVideo, items);
            ensureFolder(sourceVideo.folder).videos.push(videoNode);
            allVideos.push(videoNode);

            const recentVideoNode = createVideoNode(sourceVideo, items, true);
            if (recentVideoNode.linkedItems.length > 0) {
                recentVideos.push(recentVideoNode);
            }
        } else {
            const fallbackVideo = {
                key: videoKey || `unavailable-${unavailableVideos.length}`,
                displayName: "Source video unavailable",
                missingSource: true,
            };
            const unavailableVideo = createVideoNode(fallbackVideo, items);
            unavailableVideos.push(unavailableVideo);
            allVideos.push(unavailableVideo);

            const recentVideo = createVideoNode(fallbackVideo, items, true);
            if (recentVideo.linkedItems.length > 0) {
                recentVideos.push(recentVideo);
            }
        }
    });

    function sortFolder(node) {
        node.folders.sort(compareNames);
        node.videos.sort(compareVideoNames);
        node.folders.forEach(sortFolder);
    }

    sortFolder(root);

    if (unavailableVideos.length > 0) {
        root.folders.push({
            id: UNAVAILABLE_ID,
            key: UNAVAILABLE_ID,
            name: "Unavailable sources",
            special: "unavailable",
            folders: [],
            videos: unavailableVideos.sort(compareVideoNames),
        });
    }

    if (allVideos.length > 0) {
        root.folders.unshift({
            id: ALL_ITEMS_ID,
            key: ALL_ITEMS_ID,
            name: "All playlists & timelines",
            special: "all",
            folders: [],
            videos: [...allVideos].sort(compareVideoNames),
        });
    }

    if (recentVideos.length > 0) {
        root.folders.unshift({
            id: RECENT_ID,
            key: RECENT_ID,
            name: "Recently opened",
            special: "recent",
            folders: [],
            videos: recentVideos.sort((a, b) => {
                const aLatest = Math.max(...a.linkedItems.map(lastOpenedValue));
                const bLatest = Math.max(...b.linkedItems.map(lastOpenedValue));
                return bLatest - aLatest;
            }),
        });
    }

    return {
        root,
        itemCount: allItems.length,
        videoCount: itemsByVideo.size,
    };
}

function countLabel(column) {
    if (column.type === "items") {
        const count = column.items.length;
        return `${count} ${count === 1 ? "item" : "items"}`;
    }

    const folderCount = column.folders.length;
    const videoCount = column.videos.length;
    const parts = [];
    if (folderCount) {
        parts.push(
            `${folderCount} ${folderCount === 1 ? "folder" : "folders"}`,
        );
    }
    if (videoCount) {
        parts.push(`${videoCount} ${videoCount === 1 ? "video" : "videos"}`);
    }
    return parts.join(" · ") || "No items";
}

function formatLastOpened(value) {
    if (!value) return null;
    const parsed = dayjs(value);
    if (!parsed.isValid()) return null;
    return `Opened ${parsed.format("D MMM YYYY, HH:mm")}`;
}

function thumbnailForVideo(video) {
    if (video.missingSource) return undefined;
    const libraryKey = video.original || video.key;
    return `https://vz-db0e7359-d70.b-cdn.net/${libraryKey}/thumbnail.jpg`;
}

function breadcrumbLabel(path) {
    return path.length
        ? path.map((folder) => folder.name).join(" / ")
        : "Everything";
}

export function buildLibrarySearchIndex(root) {
    const results = {
        folders: [],
        videos: [],
        playlists: [],
        timelines: [],
    };
    const seenFolders = new Set();
    const seenVideos = new Set();
    const seenItems = new Set();

    function visit(node, path = []) {
        function visitFolder(folder) {
            const folderPath = [...path, folder];
            if (!seenFolders.has(folder.id)) {
                seenFolders.add(folder.id);
                results.folders.push({
                    type: "folder",
                    key: folder.id,
                    name: folder.name,
                    node: folder,
                    path: folderPath,
                    breadcrumb: breadcrumbLabel(path),
                });
            }
            visit(folder, folderPath);
        }

        const childFolders = [...(node.folders || [])].sort(compareNames);
        childFolders
            .filter((folder) => !folder.special)
            .forEach(visitFolder);

        (node.videos || []).forEach((video) => {
            if (!seenVideos.has(video.key)) {
                seenVideos.add(video.key);
                results.videos.push({
                    type: "video",
                    key: video.key,
                    name: video.displayName || "Untitled video",
                    node: video,
                    path,
                    breadcrumb: breadcrumbLabel(path),
                });
            }

            (video.linkedItems || []).forEach((item) => {
                const key = itemKey(item);
                if (seenItems.has(key)) return;
                seenItems.add(key);

                const result = {
                    type: item.kind,
                    key,
                    name: item.name || `Untitled ${item.kind}`,
                    item,
                    video,
                    path,
                    breadcrumb: `${video.displayName || "Untitled video"} · ${breadcrumbLabel(path)}`,
                    sourceUnavailable: Boolean(video.missingSource),
                };
                results[
                    item.kind === "playlist" ? "playlists" : "timelines"
                ].push(result);
            });
        });

        childFolders.filter((folder) => folder.special).forEach(visitFolder);
    }

    visit(root);
    return results;
}

export function searchLibrary(searchIndex, query) {
    const term = query.trim().toLocaleLowerCase();
    if (!term) return null;

    return Object.fromEntries(
        Object.entries(searchIndex).map(([group, results]) => [
            group,
            results.filter((result) =>
                result.name.toLocaleLowerCase().includes(term),
            ),
        ]),
    );
}

function BrowserRow({
    classes,
    title,
    meta,
    icon,
    image,
    selected,
    ancestor,
    ownership,
    creatorName,
    showArrow = true,
    onClick,
    onContextMenu,
}) {
    const rowClasses = [classes.row];
    if (selected) rowClasses.push(classes.selectedRow);
    else if (ancestor) rowClasses.push(classes.ancestorRow);

    return (
        <div className={classes.rowContainer}>
            <ListItemButton
                className={rowClasses.join(" ")}
                onClick={onClick}
                onContextMenu={onContextMenu}
            >
                {image ? (
                    <Avatar
                        variant="rounded"
                        src={image}
                        className={classes.thumbnail}
                    >
                        {icon}
                    </Avatar>
                ) : (
                    <span className={classes.iconTile}>{icon}</span>
                )}
                <span className={classes.rowText}>
                    <span className={classes.rowTitle}>{title}</span>
                    {(ownership || meta) && (
                        <span className={classes.rowDetails}>
                            {ownership && (
                                <span
                                    className={`${classes.ownershipBadge} ${
                                        ownership === "shared"
                                            ? classes.sharedBadge
                                            : ""
                                    }`}
                                >
                                    {ownership === "shared"
                                        ? creatorName
                                            ? `Created by ${creatorName}`
                                            : "Created by unknown user"
                                        : "Yours"}
                                </span>
                            )}
                            {meta && (
                                <span className={classes.rowMeta}>{meta}</span>
                            )}
                        </span>
                    )}
                </span>
                {showArrow && (
                    <ChevronRightRoundedIcon className={classes.arrow} />
                )}
            </ListItemButton>
        </div>
    );
}

function SearchResults({
    classes,
    query,
    results,
    onResultSelect,
    onItemContextMenu,
}) {
    const groups = [
        {
            key: "folders",
            label: "Folders",
            icon: <FolderRoundedIcon />,
        },
        { key: "videos", label: "Videos", icon: <MovieRoundedIcon /> },
        {
            key: "playlists",
            label: "Playlists",
            icon: <PlaylistPlayRoundedIcon />,
        },
        {
            key: "timelines",
            label: "Timelines",
            icon: <TimelineRoundedIcon />,
        },
    ];
    const resultCount = groups.reduce(
        (count, group) => count + results[group.key].length,
        0,
    );

    return (
        <div className={classes.searchResults}>
            <div className={classes.searchResultsCard}>
                <div className={classes.searchResultsHeader}>
                    <div className={classes.searchResultsTitle}>
                        Search results
                    </div>
                    <div className={classes.searchResultsMeta}>
                        {resultCount
                            ? `${resultCount} ${resultCount === 1 ? "match" : "matches"} for “${query.trim()}”`
                            : `No matches for “${query.trim()}”`}
                    </div>
                </div>

                {resultCount > 0 ? (
                    groups.map(
                        (group) =>
                            results[group.key].length > 0 && (
                                <React.Fragment key={group.key}>
                                    <div className={classes.sectionLabel}>
                                        {group.label}
                                    </div>
                                    {results[group.key].map((result) => (
                                        <BrowserRow
                                            key={`${result.type}:${result.key}`}
                                            classes={classes}
                                            title={result.name}
                                            meta={result.breadcrumb}
                                            image={
                                                result.type === "video"
                                                    ? thumbnailForVideo(
                                                          result.node,
                                                      )
                                                    : undefined
                                            }
                                            icon={group.icon}
                                            ownership={
                                                result.item
                                                    ? result.item.isOwner
                                                        ? "owner"
                                                        : "shared"
                                                    : undefined
                                            }
                                            creatorName={
                                                result.item?.creatorName
                                            }
                                            showArrow={
                                                result.type === "folder" ||
                                                result.type === "video"
                                            }
                                            onClick={() =>
                                                onResultSelect(result)
                                            }
                                            onContextMenu={
                                                result.item
                                                    ? (event) =>
                                                          onItemContextMenu(
                                                              event,
                                                              result.item,
                                                          )
                                                    : undefined
                                            }
                                        />
                                    ))}
                                </React.Fragment>
                            ),
                    )
                ) : (
                    <div className={classes.emptyState}>
                        <SearchRoundedIcon className={classes.emptyIcon} />
                        <Typography variant="body2">
                            Try another video, folder, playlist, or timeline
                            name.
                        </Typography>
                    </div>
                )}
            </div>
        </div>
    );
}

function UnavailableVideoDialog({
    item,
    open,
    exportInProgress,
    onClose,
    onDeleteItem,
    onExportTimeline,
}) {
    if (!item) return null;

    const isPlaylist = item.kind === "playlist";

    return (
        <Dialog
            open={open}
            onClose={exportInProgress ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "warning.dark",
                        backgroundColor: "rgba(237, 108, 2, 0.1)",
                        borderRadius: 2,
                    }}
                >
                    <WarningAmberOutlinedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Video unavailable
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {item.name}
                    </Typography>
                </Box>
            </Box>
            <DialogContent
                dividers
                sx={{
                    backgroundColor: "background.default",
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Typography variant="body2">
                    The source video linked to this {item.kind} is no longer
                    available, so it cannot be opened.
                </Typography>
                {!item.isOwner && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 1 }}
                    >
                        Only the {item.kind} owner can delete it.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    flexWrap: "wrap",
                    gap: 0.5,
                }}
            >
                <Button
                    onClick={onClose}
                    color="inherit"
                    disabled={exportInProgress}
                >
                    OK
                </Button>
                {isPlaylist ? (
                    <Button
                        onClick={onDeleteItem}
                        color="error"
                        variant="contained"
                        disableElevation
                        disabled={!item.isOwner}
                        startIcon={<DeleteOutlineIcon />}
                    >
                        Delete playlist
                    </Button>
                ) : (
                    <>
                        <Button
                            onClick={onDeleteItem}
                            color="error"
                            disabled={!item.isOwner || exportInProgress}
                            startIcon={<DeleteOutlineIcon />}
                        >
                            Delete timeline
                        </Button>
                        <Button
                            onClick={onExportTimeline}
                            variant="contained"
                            disableElevation
                            disabled={exportInProgress}
                            startIcon={
                                exportInProgress ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                ) : (
                                    <FileDownloadOutlinedIcon />
                                )
                            }
                        >
                            {exportInProgress
                                ? "Preparing XML…"
                                : "Export XML timeline"}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

function LibraryColumn({
    classes,
    column,
    index,
    selectedColumnIndex,
    onFolderSelect,
    onVideoSelect,
    onItemSelect,
    onItemContextMenu,
    isLoading,
}) {
    const selectedKey = column.selectedKey;

    return (
        <div className={classes.column}>
            <div className={classes.titleRow}>
                <div className={classes.titleBlock}>
                    <span className={classes.columnTitle} title={column.name}>
                        {column.name}
                    </span>
                    <span className={classes.columnMeta}>
                        {isLoading ? "Loading…" : countLabel(column)}
                    </span>
                </div>
            </div>

            {isLoading ? (
                <div className={classes.emptyState}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading your library…
                    </Typography>
                </div>
            ) : column.type === "items" ? (
                <>
                    {column.items.some((item) => item.kind === "playlist") && (
                        <div className={classes.sectionLabel}>Playlists</div>
                    )}
                    {column.items
                        .filter((item) => item.kind === "playlist")
                        .map((item) => (
                            <BrowserRow
                                key={`playlist-${item.key}`}
                                classes={classes}
                                title={item.name}
                                meta={formatLastOpened(item.lastOpened)}
                                ownership={
                                    item.isOwner ? "owner" : "shared"
                                }
                                creatorName={item.creatorName}
                                icon={<PlaylistPlayRoundedIcon />}
                                showArrow={false}
                                onClick={() =>
                                    onItemSelect(item, column.sourceUnavailable)
                                }
                                onContextMenu={(event) =>
                                    onItemContextMenu(event, item)
                                }
                            />
                        ))}

                    {column.items.some((item) => item.kind === "timeline") && (
                        <div className={classes.sectionLabel}>Timelines</div>
                    )}
                    {column.items
                        .filter((item) => item.kind === "timeline")
                        .map((item) => (
                            <BrowserRow
                                key={`timeline-${item.key}`}
                                classes={classes}
                                title={item.name}
                                meta={formatLastOpened(item.lastOpened)}
                                ownership={
                                    item.isOwner ? "owner" : "shared"
                                }
                                creatorName={item.creatorName}
                                icon={<TimelineRoundedIcon />}
                                showArrow={false}
                                onClick={() =>
                                    onItemSelect(item, column.sourceUnavailable)
                                }
                                onContextMenu={(event) =>
                                    onItemContextMenu(event, item)
                                }
                            />
                        ))}
                </>
            ) : (
                <>
                    {column.folders.length > 0 && (
                        <div className={classes.sectionLabel}>Folders</div>
                    )}
                    {column.folders.map((folder) => {
                        const itemKey = `folder:${folder.id}`;
                        const isSelected = selectedKey === itemKey;
                        const icon =
                            folder.special === "recent" ? (
                                <AccessTimeRoundedIcon />
                            ) : folder.special === "all" ? (
                                <PlaylistPlayRoundedIcon />
                            ) : folder.special === "unavailable" ? (
                                <VideocamOffRoundedIcon />
                            ) : (
                                <FolderRoundedIcon />
                            );

                        return (
                            <BrowserRow
                                key={itemKey}
                                classes={classes}
                                title={folder.name}
                                meta={
                                    folder.special === "recent"
                                        ? "Your latest playlists and timelines"
                                        : folder.special === "all"
                                          ? "Everything you can open, by video"
                                          : folder.special === "unavailable"
                                            ? "Items whose source video is missing"
                                            : null
                                }
                                icon={icon}
                                selected={
                                    isSelected && selectedColumnIndex === index
                                }
                                ancestor={
                                    isSelected && selectedColumnIndex > index
                                }
                                onClick={() => onFolderSelect(index, folder)}
                            />
                        );
                    })}

                    {column.videos.length > 0 && (
                        <div className={classes.sectionLabel}>Videos</div>
                    )}
                    {column.videos.map((video) => {
                        const itemKey = `video:${video.key}`;
                        const isSelected = selectedKey === itemKey;

                        return (
                            <BrowserRow
                                key={`${itemKey}:${video.recentOnly ? "recent" : "all"}`}
                                classes={classes}
                                title={video.displayName || "Untitled video"}
                                meta={`${video.linkedItems.length} ${
                                    video.linkedItems.length === 1
                                        ? "linked item"
                                        : "linked items"
                                }`}
                                image={thumbnailForVideo(video)}
                                icon={
                                    video.missingSource ? (
                                        <VideocamOffRoundedIcon />
                                    ) : (
                                        <MovieRoundedIcon />
                                    )
                                }
                                selected={
                                    isSelected && selectedColumnIndex === index
                                }
                                ancestor={
                                    isSelected && selectedColumnIndex > index
                                }
                                onClick={() => onVideoSelect(index, video)}
                            />
                        );
                    })}

                    {column.folders.length === 0 &&
                        column.videos.length === 0 && (
                            <div className={classes.emptyState}>
                                <PlaylistPlayRoundedIcon
                                    className={classes.emptyIcon}
                                />
                                <Typography variant="body2">
                                    No playlists or timelines are available here
                                    yet.
                                </Typography>
                            </div>
                        )}
                </>
            )}
        </div>
    );
}

function columnForNode(node) {
    return {
        id: node.id || node.key,
        name: node.name,
        type: "browser",
        folders: node.folders || [],
        videos: node.videos || [],
        selectedKey: null,
    };
}

function itemColumnForVideo(video) {
    return {
        id: `linked-items:${video.key}:${video.recentOnly ? "recent" : "all"}`,
        name: video.displayName || "Untitled video",
        type: "items",
        items: video.linkedItems,
        sourceUnavailable: Boolean(video.missingSource),
        selectedKey: null,
    };
}

function itemKey(item) {
    return `${item.kind}:${item.key}`;
}

export function rebuildLibraryColumns(root, currentColumns) {
    if (!currentColumns?.length) return [columnForNode(root)];

    const nextColumns = [];
    let currentNode = root;

    for (let index = 0; index < currentColumns.length; index += 1) {
        const previousColumn = currentColumns[index];
        const nextColumn = columnForNode(currentNode);
        const selectedKey = previousColumn.selectedKey;
        nextColumns.push(nextColumn);

        if (!selectedKey) return nextColumns;

        if (selectedKey.startsWith("folder:")) {
            const folderKey = selectedKey.slice("folder:".length);
            const selectedFolder = currentNode.folders.find(
                (folder) => folder.id === folderKey,
            );

            if (!selectedFolder) {
                return nextColumns;
            }

            nextColumn.selectedKey = selectedKey;
            currentNode = selectedFolder;
            continue;
        }

        if (selectedKey.startsWith("video:")) {
            const videoKey = selectedKey.slice("video:".length);
            const selectedVideo = currentNode.videos.find(
                (video) => video.key === videoKey,
            );

            if (!selectedVideo) {
                return nextColumns;
            }

            nextColumn.selectedKey = selectedKey;
            nextColumns.push(itemColumnForVideo(selectedVideo));
            return nextColumns;
        }

        return nextColumns;
    }

    return nextColumns;
}

export default function PlaylistsAndTimelinesPage() {
    const classes = useStyles();
    const history = useHistory();
    const scrollRef = useRef();
    const { currentUserData } = useContext(UserContext);
    const {
        files,
        filesLoaded,
        folders,
        foldersLoaded,
        playlistInfo,
        playlistInfoLoaded,
        playlistUsage,
        playlistUsageLoaded,
        timelineInfo,
        timelineInfoLoaded,
        timelineUsage,
        timelineUsageLoaded,
        getTimelines,
        groups,
        groupsLoaded,
        users,
    } = useContext(DatabaseContext);

    const library = useMemo(
        () =>
            buildPlaylistsAndTimelinesLibrary({
                files,
                folders,
                playlistInfo,
                playlistUsage,
                timelineInfo,
                timelineUsage,
                groups,
                users,
                currentUserData,
            }),
        [
            files,
            folders,
            playlistInfo,
            playlistUsage,
            timelineInfo,
            timelineUsage,
            groups,
            users,
            currentUserData,
        ],
    );
    const searchIndex = useMemo(
        () => buildLibrarySearchIndex(library.root),
        [library],
    );

    const [columns, setColumns] = useState([columnForNode(library.root)]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedColumnIndex, setSelectedColumnIndex] = useState(-1);
    const [contextMenu, setContextMenu] = useState(null);
    const [renameItem, setRenameItem] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);
    const [unavailableItem, setUnavailableItem] = useState(null);
    const [exportInProgress, setExportInProgress] = useState(false);
    const [deleteInProgress, setDeleteInProgress] = useState(false);
    const [status, setStatus] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const isLibraryLoading =
        !currentUserData ||
        !filesLoaded ||
        !foldersLoaded ||
        !groupsLoaded ||
        !playlistInfoLoaded ||
        !playlistUsageLoaded ||
        !timelineInfoLoaded ||
        !timelineUsageLoaded;
    const searchResults = useMemo(
        () => searchLibrary(searchIndex, searchQuery),
        [searchIndex, searchQuery],
    );

    useLayoutEffect(() => {
        setColumns((currentColumns) =>
            rebuildLibraryColumns(library.root, currentColumns),
        );
    }, [library]);

    useEffect(() => {
        const frameId = window.requestAnimationFrame(() => {
            scrollRef.current?.scrollToRight();
        });
        return () => window.cancelAnimationFrame(frameId);
    }, [columns.length]);

    function openNextColumn(columnIndex, selectedKey, nextColumn) {
        setColumns((currentColumns) => {
            const nextColumns = currentColumns
                .slice(0, columnIndex + 1)
                .map((column) => ({ ...column }));
            nextColumns[columnIndex].selectedKey = selectedKey;
            nextColumns.push(nextColumn);
            return nextColumns;
        });
        setSelectedColumnIndex(columnIndex);
    }

    function handleFolderSelect(columnIndex, folder) {
        openNextColumn(
            columnIndex,
            `folder:${folder.id}`,
            columnForNode(folder),
        );
    }

    function handleVideoSelect(columnIndex, video) {
        openNextColumn(
            columnIndex,
            `video:${video.key}`,
            itemColumnForVideo(video),
        );
    }

    function openSearchPath(result) {
        const nextColumns = [columnForNode(library.root)];
        let currentNode = library.root;

        for (const pathFolder of result.path) {
            const folder = currentNode.folders.find(
                (candidate) => candidate.id === pathFolder.id,
            );
            if (!folder) return;

            nextColumns[nextColumns.length - 1].selectedKey =
                `folder:${folder.id}`;
            nextColumns.push(columnForNode(folder));
            currentNode = folder;
        }

        if (result.type === "video") {
            const video = currentNode.videos.find(
                (candidate) => candidate.key === result.node.key,
            );
            if (!video) return;

            nextColumns[nextColumns.length - 1].selectedKey =
                `video:${video.key}`;
            nextColumns.push(itemColumnForVideo(video));
            setSelectedColumnIndex(result.path.length);
        } else {
            setSelectedColumnIndex(Math.max(0, result.path.length - 1));
        }

        setColumns(nextColumns);
        setSearchQuery("");
    }

    function handleSearchResultSelect(result) {
        if (result.type === "folder" || result.type === "video") {
            openSearchPath(result);
            return;
        }

        setSearchQuery("");
        handleItemSelect(result.item, result.sourceUnavailable);
    }

    function handleItemSelect(item, sourceUnavailable) {
        if (sourceUnavailable) {
            setUnavailableItem(item);
            return;
        }

        if (item.kind === "playlist") {
            history.push(`/playlist?key=${item.key}`);
            return;
        }

        history.push(`/analysis?video=${item.video}&key=${item.key}`);
    }

    function handleUnavailableDialogClose() {
        if (!exportInProgress) {
            setUnavailableItem(null);
        }
    }

    function handleUnavailableItemDelete() {
        if (!unavailableItem?.isOwner) return;
        setDeleteItem(unavailableItem);
        setUnavailableItem(null);
    }

    async function handleUnavailableTimelineExport() {
        if (!unavailableItem || unavailableItem.kind !== "timeline") return;

        setExportInProgress(true);

        try {
            const timelineDetails = await getTimelines(true);
            const timeline = timelineDetails.find(
                (candidate) => candidate.key === unavailableItem.key,
            );

            if (!timeline) {
                throw new Error("Timeline details are unavailable");
            }

            exportTimelineToXML(timeline.instances, timeline.name);
            setUnavailableItem(null);
            showStatus("Timeline XML exported", "success");
        } catch (error) {
            console.error("Unable to export timeline XML", error);
            showStatus("Unable to export timeline XML", "error");
        } finally {
            setExportInProgress(false);
        }
    }

    function handleItemContextMenu(event, item) {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 4,
            item,
        });
    }

    function closeContextMenu() {
        setContextMenu(null);
    }

    function handleRenameMenuItem() {
        if (!contextMenu?.item?.isOwner) return;
        setRenameItem(contextMenu.item);
        closeContextMenu();
    }

    function handleDeleteMenuItem() {
        if (!contextMenu?.item?.isOwner) return;
        setDeleteItem(contextMenu.item);
        closeContextMenu();
    }

    function showStatus(message, severity) {
        setStatus({ open: true, message, severity });
    }

    async function handleRenameDialogClose(newName, confirmed) {
        const item = renameItem;
        setRenameItem(null);

        if (!confirmed || !item?.isOwner) return;

        const trimmedName = newName.trim();
        if (!trimmedName || trimmedName === item.name) return;

        const collection = item.kind === "playlist" ? "playlists" : "timelines";
        const infoCollection =
            item.kind === "playlist" ? "playlistinfo" : "timelineinfo";

        try {
            await Promise.all([
                firebase
                    .database()
                    .ref(collection)
                    .child(item.key)
                    .update({ name: trimmedName }),
                firebase
                    .database()
                    .ref(infoCollection)
                    .child(item.key)
                    .update({ name: trimmedName }),
            ]);
            showStatus(
                `${item.kind === "playlist" ? "Playlist" : "Timeline"} renamed`,
                "success",
            );
        } catch (error) {
            console.error(`Unable to rename ${item.kind}`, error);
            showStatus(`Unable to rename ${item.kind}`, "error");
        }
    }

    async function handleDeleteDialogClose(confirmed) {
        if (!confirmed) {
            setDeleteItem(null);
            return;
        }

        const item = deleteItem;
        if (!item?.isOwner) {
            setDeleteItem(null);
            return;
        }

        setDeleteInProgress(true);

        try {
            await deletePlaylistOrTimelineRecord(item.kind, item.key);
            setDeleteItem(null);
            showStatus(
                `${item.kind === "playlist" ? "Playlist" : "Timeline"} deleted`,
                "success",
            );
        } catch (error) {
            console.error(`Unable to delete ${item.kind}`, error);
            showStatus(`Unable to delete ${item.kind}`, "error");
        } finally {
            setDeleteInProgress(false);
        }
    }

    function handleStatusClose(event, reason) {
        if (reason === "clickaway") return;
        setStatus((current) => ({ ...current, open: false }));
    }

    return (
        <div className={classes.root}>
            <div className={classes.searchBar}>
                <TextField
                    className={classes.searchField}
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search videos, folders, playlists and timelines"
                    disabled={isLibraryLoading}
                    autoComplete="off"
                    inputProps={{ "aria-label": "Search library" }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRoundedIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="Clear search"
                                    edge="end"
                                    size="small"
                                    onClick={() => setSearchQuery("")}
                                >
                                    <CloseRoundedIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined,
                    }}
                />
            </div>
            <Scrollbars
                className={classes.libraryScroll}
                renderView={(viewProps) => (
                    <div
                        {...viewProps}
                        className={`${viewProps.className || ""} ${classes.scrollView}`}
                    />
                )}
                ref={scrollRef}
            >
                {searchResults ? (
                    <SearchResults
                        classes={classes}
                        query={searchQuery}
                        results={searchResults}
                        onResultSelect={handleSearchResultSelect}
                        onItemContextMenu={handleItemContextMenu}
                    />
                ) : (
                    <div className={classes.columnsContainer}>
                        {columns.map((column, index) => (
                            <div className={classes.columnShell} key={column.id}>
                                <LibraryColumn
                                    classes={classes}
                                    column={column}
                                    index={index}
                                    selectedColumnIndex={selectedColumnIndex}
                                    onFolderSelect={handleFolderSelect}
                                    onVideoSelect={handleVideoSelect}
                                    onItemSelect={handleItemSelect}
                                    onItemContextMenu={handleItemContextMenu}
                                    isLoading={isLibraryLoading && index === 0}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </Scrollbars>
            <Menu
                open={Boolean(contextMenu)}
                onClose={closeContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu
                        ? {
                              top: contextMenu.mouseY,
                              left: contextMenu.mouseX,
                          }
                        : undefined
                }
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: 190,
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            boxShadow: "0 14px 36px rgba(20, 35, 25, 0.16)",
                            "& .MuiMenu-list": { padding: 0.75 },
                            "& .MuiMenuItem-root": {
                                minHeight: 40,
                                borderRadius: 1.5,
                            },
                        },
                    },
                }}
            >
                <MenuItem
                    onClick={handleRenameMenuItem}
                    disabled={!contextMenu?.item?.isOwner}
                >
                    <ListItemIcon>
                        <EditOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    Rename {contextMenu?.item?.kind || "item"}
                </MenuItem>
                <MenuItem
                    onClick={handleDeleteMenuItem}
                    disabled={!contextMenu?.item?.isOwner}
                    sx={{ color: "error.main" }}
                >
                    <ListItemIcon sx={{ color: "inherit" }}>
                        <DeleteOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    Delete {contextMenu?.item?.kind || "item"}
                </MenuItem>
                {contextMenu?.item && !contextMenu.item.isOwner && (
                    <MenuItem disabled sx={{ fontSize: 12 }}>
                        Only the owner can manage this item
                    </MenuItem>
                )}
            </Menu>
            {renameItem && (
                <DialogWithTextInput
                    open={Boolean(renameItem)}
                    onClose={handleRenameDialogClose}
                    title={`Rename ${
                        renameItem.kind === "playlist" ? "Playlist" : "Timeline"
                    }`}
                    message={`Enter a new name for this ${renameItem.kind}.`}
                    initialValue={renameItem.name}
                    confirmLabel="Rename"
                    modern
                />
            )}
            {deleteItem && (
                <ConfirmDeleteDialog
                    open={Boolean(deleteItem)}
                    onClose={handleDeleteDialogClose}
                    title={`Delete ${
                        deleteItem.kind === "playlist" ? "Playlist" : "Timeline"
                    }`}
                    detail={deleteItem.name}
                    message={`Are you sure you want to delete this ${deleteItem.kind}?`}
                    inProgress={deleteInProgress}
                />
            )}
            <UnavailableVideoDialog
                item={unavailableItem}
                open={Boolean(unavailableItem)}
                exportInProgress={exportInProgress}
                onClose={handleUnavailableDialogClose}
                onDeleteItem={handleUnavailableItemDelete}
                onExportTimeline={handleUnavailableTimelineExport}
            />
            <Snackbar
                open={status.open}
                autoHideDuration={5000}
                onClose={handleStatusClose}
            >
                <Alert onClose={handleStatusClose} severity={status.severity}>
                    {status.message}
                </Alert>
            </Snackbar>
            <ToolbarWithLogo />
        </div>
    );
}
